import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Clock, Tag } from 'lucide-react';

export const blogArticles = [
  {
    slug: 'grossistes-fournisseurs-vinted',
    title: 'Grossistes & fournisseurs Vinted : ce que personne ne te dit vraiment',
    excerpt: 'Avant de commander pour 5 000 € de stock, lis ça. On te dit tout sur les vrais fournisseurs privés, les arnaques à fuir et pourquoi 90 % des "grossistes" vantés sur les réseaux sont des pièges.',
    category: 'Stratégie Vinted',
    readTime: '5 min',
    date: '20 avril 2026',
    emoji: '📦',
  },
  {
    slug: 'boost-vinted-utile-ou-mythe',
    title: 'Boost Vinted : utile ou mythe ? La réponse honnête',
    excerpt: 'Le boost augmente ta visibilité, c\'est vrai. Mais sur Vinted, les acheteurs comparent toujours les prix et négocient même pour 2 €. On te dit dans quels cas ça vaut le coup et quand c\'est de l\'argent jeté.',
    category: 'Stratégie Vinted',
    readTime: '5 min',
    date: '13 avril 2026',
    emoji: '🚀',
  },
  {
    slug: 'bots-vinted-autobuy-autocop',
    title: 'Bot Vinted : autobuy vs autocop, tout ce qu\'il faut savoir',
    excerpt: 'La concurrence sur Vinted est devenue féroce. Autobuy ou autocop ? On compare les meilleures plateformes, leurs filtres, leurs risques et ce qui différencie vraiment ces bots.',
    category: 'Stratégie Vinted',
    readTime: '6 min',
    date: '6 avril 2026',
    emoji: '🤖',
  },
];

const BlogPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Blog Vinted : Conseils pour revendeurs | SmartResell</title>
        <meta name="description" content="Stratégies, conseils et analyses pour les revendeurs Vinted. Des articles concrets écrits sans bullshit." />
        <link rel="canonical" href="https://www.smart-resell.com/blog" />
        <meta property="og:title" content="Blog Vinted : Conseils pour revendeurs | SmartResell" />
        <meta property="og:description" content="Stratégies, conseils et analyses pour les revendeurs Vinted. Des articles concrets écrits sans bullshit." />
        <meta property="og:url" content="https://www.smart-resell.com/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.smart-resell.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "Blog SmartResell",
          "description": "Stratégies, conseils et analyses pour les revendeurs Vinted.",
          "url": "https://www.smart-resell.com/blog",
          "inLanguage": "fr",
          "publisher": { "@type": "Organization", "name": "SmartResell", "url": "https://www.smart-resell.com" }
        })}</script>
      </Helmet>

      {/* Header */}
      <header className="border-b border-gray-100 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <img src="/logo192.png" alt="SmartResell" className="w-8 h-8 rounded-xl" />
            <span className="font-bold text-lg" style={{ color: '#0d9488' }}>SmartResell</span>
          </div>
          <button
            onClick={() => navigate('/')}
            className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            ← Retour
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="mb-14">
          <span className="inline-block text-xs font-bold text-teal-600 bg-teal-50 px-3 py-1 rounded-full mb-4 uppercase tracking-widest">Blog</span>
          <h1 className="text-4xl font-bold text-gray-900 mb-4 leading-tight">
            Conseils & stratégies pour<br />les revendeurs Vinted
          </h1>
          <p className="text-lg text-gray-500 max-w-xl">
            Des articles concrets écrits par des resellers, pour des resellers. Pas de bullshit, que de la valeur.
          </p>
        </div>

        {/* Articles */}
        <div className="grid gap-6">
          {blogArticles.map((article) => (
            <article
              key={article.slug}
              onClick={() => navigate(`/blog/${article.slug}`)}
              className="group bg-white border border-gray-100 rounded-2xl p-7 cursor-pointer hover:border-teal-200 hover:shadow-lg transition-all duration-200"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs font-semibold text-teal-600 bg-teal-50 px-2.5 py-1 rounded-full flex items-center gap-1">
                      <Tag size={11} />{article.category}
                    </span>
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Clock size={11} />{article.readTime}
                    </span>
                    <span className="text-xs text-gray-400">{article.date}</span>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-700 transition-colors leading-snug">
                    {article.emoji} {article.title}
                  </h2>
                  <p className="text-gray-500 text-sm leading-relaxed">{article.excerpt}</p>
                </div>
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gray-50 group-hover:bg-teal-50 flex items-center justify-center transition-colors">
                  <ArrowRight size={18} className="text-gray-400 group-hover:text-teal-600 transition-colors" />
                </div>
              </div>
            </article>
          ))}
        </div>
      </main>

      <footer className="border-t border-gray-100 mt-16 py-8 text-center text-sm text-gray-400">
        <span
          className="font-semibold cursor-pointer hover:text-teal-600 transition-colors"
          onClick={() => navigate('/')}
          style={{ color: '#0d9488' }}
        >
          SmartResell
        </span>
        , l'outil des resellers Vinted pro
      </footer>
    </div>
  );
};

export default BlogPage;
